import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BalancePicture as BalancePictureData } from '@/api/models';
import { OVERVIEW_ERROR_KEYS } from '../overview';
import { BalancePictureView } from './BalancePictureView';
import { useBalancePicture } from './useBalancePicture';

/**
 * Self-served structural strip: its own slice of the read, its own
 * `ErrorBoundary`.
 *
 * Renders **nothing** when the read is pending. There is no structural view of a
 * ledger with nothing posted to it, and the design says so by leaving the
 * section out of the empty artboard entirely — five dashes would be a section
 * pretending to report.
 */
export const BalancePicture: React.FC = () => {
    const page = useBalancePicture();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t(OVERVIEW_ERROR_KEYS.title),
            errorMessage: t(OVERVIEW_ERROR_KEYS.message),
            errorRetry: t(OVERVIEW_ERROR_KEYS.retry),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="balance-picture-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="balance-picture-error-boundary"
            onRetry={page.onRetry}
            resetKeys={[page.error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <BalancePictureContent
                data={page.data}
                error={page.error}
                isLoading={page.isLoading}
            />
        </ErrorBoundary>
    );
};

type BalancePictureContentProps = {
    data: BalancePictureData | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/** Inside the boundary, so the throw below is caught by it. */
const BalancePictureContent: React.FC<BalancePictureContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading) {
        return <Skeleton className="h-32 w-full rounded-xl" />;
    }

    if (!data) {
        return null;
    }

    return <BalancePictureView data={data} />;
};
